// A4/E3 — gợi ý `@Tên` và `#MÃ-DỰ-ÁN` trong ô soạn tin: logic thuần để Main dùng và test E2E import trực tiếp.
import { normalizeSearch } from '@swchat/client-sdk';

/** Độ dài tối đa phần gõ sau `@` còn coi là đang tìm thành viên. */
const MAX_QUERY = 40;
/** Mã dự án: chữ/số, `-`, `_` (khớp `TAG_RE` của SDK, không phân biệt hoa thường khi đang gõ). */
const TAG_QUERY_RE = /^[\p{L}\p{N}_-]{0,32}$/u;

/**
 * Phần đang gõ sau `@` ngay trước con trỏ, hoặc `undefined` nếu không ở trong mention.
 * Tên tiếng Việt thường nhiều chữ ("Trần Bình") nên cho phép khoảng trắng — nhưng chỉ khi phần đã gõ vẫn là
 * tiền tố tên của ít nhất một thành viên (gõ "@Trần B" vẫn lọc; "@Trần rồi nói tiếp…" thì thôi gợi ý).
 */
export function mentionQuery(beforeCaret: string, names: readonly string[]): string | undefined {
  const at = beforeCaret.lastIndexOf('@');
  if (at < 0 || (at > 0 && !/\s/.test(beforeCaret[at - 1]!))) return undefined;
  const query = beforeCaret.slice(at + 1);
  if (query.length > MAX_QUERY || /[@\n]/.test(query)) return undefined;
  if (!/\s/.test(query)) return query;
  const q = normalizeSearch(query);
  return names.some((n) => normalizeSearch(n).startsWith(q)) ? query : undefined;
}

/** Thành viên khớp phần đã gõ (không dấu, không phân biệt hoa thường), tối đa `limit`. */
export function mentionSuggestions<M extends { name: string }>(query: string, members: readonly M[], limit = 6): M[] {
  const q = normalizeSearch(query);
  return members.filter((m) => normalizeSearch(m.name).includes(q)).slice(0, limit);
}

/**
 * E3 — phần đang gõ sau `#` ngay trước con trỏ (mã dự án không có khoảng trắng), hoặc `undefined`. `#` phải đứng đầu
 * hoặc sau khoảng trắng (`abc#x` không phải tag).
 */
export function tagQuery(beforeCaret: string): string | undefined {
  const at = beforeCaret.lastIndexOf('#');
  if (at < 0 || (at > 0 && !/\s/.test(beforeCaret[at - 1]!))) return undefined;
  const query = beforeCaret.slice(at + 1);
  return TAG_QUERY_RE.test(query) ? query : undefined;
}

/** Dự án có mã hoặc tên khớp phần đã gõ (mã khớp tiền tố xếp trước), tối đa `limit`. */
export function tagSuggestions<P extends { code: string; name: string }>(query: string, projects: readonly P[], limit = 6): P[] {
  const q = normalizeSearch(query);
  const hits = projects.filter((p) => normalizeSearch(p.code).includes(q) || normalizeSearch(p.name).includes(q));
  const prefix = (p: P) => (normalizeSearch(p.code).startsWith(q) ? 0 : 1);
  return hits.sort((a, b) => prefix(a) - prefix(b)).slice(0, limit);
}

/** Thay `@query` (hoặc `#query`) trước con trỏ bằng `@Tên ` → [văn bản mới, vị trí con trỏ mới]. */
export function applyMention(text: string, caret: number, query: string, name: string, trigger: '@' | '#' = '@'): [string, number] {
  const before = `${text.slice(0, caret - query.length - 1)}${trigger}${name} `;
  return [before + text.slice(caret), before.length];
}

/** Một dòng gợi ý trong ô soạn tin. */
export interface Suggestion {
  key: string;
  label: string;
  hint?: string;
  trigger: '@' | '#';
  /** Văn bản chèn sau ký tự kích hoạt. */
  value: string;
  /** User id (chỉ với `@`) — ưu tiên khi trùng tên. */
  id?: string;
}

/**
 * A4/E3 — gợi ý của ô soạn tin theo phần trước con trỏ: `@…` gợi ý thành viên (chỉ trong nhóm), nếu không thì `#…` gợi ý
 * mã dự án. Trả về cả phần đang gõ để `pickSuggestion` thay đúng đoạn đó.
 */
export function composerSuggestions(
  beforeCaret: string,
  direct: boolean,
  members: readonly { id: string; name: string }[],
  projects: readonly { id: string; code: string; name: string }[],
): { mentionQuery?: string; hashQuery?: string; suggestions: Suggestion[] } {
  const mention = direct ? undefined : mentionQuery(beforeCaret, members.map((m) => m.name));
  if (mention !== undefined) {
    return {
      mentionQuery: mention,
      suggestions: ([
        // `@all` nhắc cả nhóm — xếp đầu khi phần đã gõ là tiền tố của "all".
        ...(members.length > 1 && 'all'.startsWith(mention.trim().toLowerCase())
          ? [{ key: '@all', label: '@all', hint: 'Nhắc tất cả thành viên', trigger: '@' as const, value: 'all' }]
          : []),
        ...mentionSuggestions(mention, members).map((m) => ({ key: m.id, label: `@${m.name}`, trigger: '@', id: m.id, value: m.name })),
      ] as Suggestion[]).slice(0, 7),
    };
  }
  const hash = tagQuery(beforeCaret);
  if (hash === undefined) return { suggestions: [] };
  return {
    hashQuery: hash,
    suggestions: tagSuggestions(hash, projects).map((p) => ({ key: p.id, label: `#${p.code}`, hint: p.name, trigger: '#', value: p.code })),
  };
}

/** Chèn gợi ý đã chọn tại con trỏ → [văn bản mới, vị trí con trỏ mới]. */
export function pickSuggestionText(
  text: string,
  caret: number,
  s: Pick<Suggestion, 'trigger' | 'value'>,
  q: { mentionQuery?: string; hashQuery?: string },
): [string, number] {
  return applyMention(text, caret, s.trigger === '@' ? (q.mentionQuery ?? '') : (q.hashQuery ?? ''), s.value, s.trigger);
}
